import axios from 'axios';
import { useEffect, useState } from 'react';
import { useDeal, searchWithAccentsAndPunctuation } from './context';
import { IconButton, Modal, Button, Spinner, InputSwitch } from '@components';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { get } from 'lodash';
import { COLOR_GROUPS_RAW } from '../utils/colors';
import { toast } from 'sonner';
import InternalDropdown from './internalDropdown';

function Item({ item, checked = false, onClick }) {
  return (
    <button
      type="button"
      className="w-full flex items-center space-x-2 py-2 px-4 hover:bg-slate-50"
      onClick={onClick}
    >
      <div className="flex items-center">
        <input
          readOnly
          checked={checked}
          type="checkbox"
          className="h-4 w-4 rounded cursor-pointer"
        />
      </div>
      <div className="text-sm font-hmedium">{item.label}</div>
    </button>
  );
}

function GroupCheckbox({ group, selectedItemIds, onToggle }) {
  const isFullySelected = group.children.every(child => selectedItemIds.includes(child.id));
  const isPartiallySelected = group.children.some(child => selectedItemIds.includes(child.id));

  return (
    <input
      readOnly
      checked={isFullySelected}
      ref={input => {
        if (input) {
          input.indeterminate = !isFullySelected && isPartiallySelected;
        }
      }}
      onClick={onToggle}
      type="checkbox"
      className="h-4 w-4 rounded cursor-pointer"
    />
  );
}

// Helper function to get the highest z-index from dialog elements
function getHighestDialogZIndex() {
  const dialogElements = document.querySelectorAll('[data-controller="dialog"]');
  let maxZIndex = 0;

  dialogElements.forEach(element => {
    const computedStyle = window.getComputedStyle(element);
    const zIndex = parseInt(computedStyle.zIndex, 10);
    
    if (!isNaN(zIndex) && zIndex > maxZIndex) {
      maxZIndex = zIndex;
    }
  });

  return maxZIndex;
}

export default function AddModal({ isOpen = false, onClose = () => {} }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchError, setIsFetchError] = useState(false);
  const [isSubmitError, setIsSubmitError] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [modalZIndex, setModalZIndex] = useState(50); // Default z-index
  const { dealId, setCurrentTaskItemFilter, currentTaskItemFilter } = useDeal();

  // Update z-index when modal opens
  useEffect(() => {
    if (isOpen) {
      const highestZIndex = getHighestDialogZIndex();
      // Set our modal's z-index to be higher than any existing dialog
      setModalZIndex(Math.max(highestZIndex + 1, 50));
    }
  }, [isOpen]);

  const renderedGroups = groups
    .filter(g => {
      if (currentTaskItemFilter === 'INTERNAL') {
        return !!g.internal;
      } else if (currentTaskItemFilter === 'PUBLIC') {
        return !g.internal;
      }

      return g;
    })
    .map(g => {
      if (String(keyword).length > 0) {
        const matchingItems = g.children.filter(item =>
          searchWithAccentsAndPunctuation(item.label, keyword)
        );

        if (matchingItems.length >= 1) {
          return { ...g, children: matchingItems };
        } else {
          return null;
        }
      }

      if (g.children.length >= 1) {
        return g;
      }
    })
    .filter(Boolean);

  function toggleItem(id) {
    setSelectedItemIds(prev => {
      if (prev.includes(id)) {
        return prev.filter(prevId => prevId !== id);
      } else {
        return [...prev, id];
      }
    });
  }

  function toggleGroup(group) {
    const childIds = group.children.map(child => child.id);
    const areAllSelected = group.children.every(child => selectedItemIds.includes(child.id));

    setSelectedItemIds(prev => {
      if (areAllSelected) {
        // Deselect all children
        return prev.filter(id => !childIds.includes(id));
      } else {
        // Select all children
        const newSelection = new Set(prev);
        childIds.forEach(id => newSelection.add(id));
        return Array.from(newSelection);
      }
    });
  }

  async function fetchGroups() {
    try {
      const { data } = await axios({
        method: 'get',
        url: `/deals/${dealId}/template_items`
      });
      setGroups(data);
    } catch (err) {
      setIsFetchError(err);
    } finally {
      setIsFetchError(false);
      setIsLoading(false);
    }
  }

  useEffect(() => {
    setSelectedItemIds([]);
  }, [currentTaskItemFilter]);

  useEffect(() => {
    fetchGroups();
  }, []);

  async function createTaskItems() {
    setIsSubmitting(true);
    try {
      await axios({
        method: 'put',
        url: `/deals/${dealId}/create_task_items`,
        headers: {
          Accept: 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml'
        },
        data: {
          deal: {
            template_item_ids: selectedItemIds
          }
        }
      });
      onClose();
      setSelectedItemIds([]);
      toast.success('Les démarches ont été ajoutées.');
    } catch (err) {
      setIsSubmitError(err);
    } finally {
      setIsSubmitError(false);
      setIsSubmitting(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} style={{ zIndex: modalZIndex }}>
      <div className="w-full rounded-lg bg-white h-[90vh] overflow-hidden relative flex flex-col !w-[700px] max-w-[90vw]">
        <div className="sticky top-0 flex-shrink-0 w-full">
          <div className="py-3 px-6 flex items-center justify-between border-b">
            <h3 className="font-hmedium tracking-tight text-lg">Ajouter un nouvel élément</h3>
            <div className="flex items-center space-x-2">
              <InternalDropdown buttonProps={{ size: 'sm' }} />
              <IconButton icon={XMarkIcon} onClick={onClose} size="lg" />
            </div>
          </div>
          <div className="border-b flex items-center space-x-3 px-4">
            {keyword.length <= 0 && <MagnifyingGlassIcon className="w-4 h-4" />}
            {keyword.length > 0 && (
              <button onClick={e => setKeyword('')}>
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}
            <input
              type="text"
              className="w-full py-2 outline-none"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              placeholder="Rechercher dans la liste"
            />
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto">
          {isFetchError && !isLoading && (
            <div className="w-full h-[300px] flex items-center justify-center">
              <div className="text-sm">
                Une erreur est survenue,
                <br />
                veuillez essayer ultérieurement.
              </div>
            </div>
          )}
          {!isFetchError && isLoading && (
            <div className="w-full h-full flex items-center justify-center">
              <Spinner />
            </div>
          )}
          {!isFetchError &&
            !isLoading &&
            renderedGroups
              .sort((a, b) => a.position - b.position)
              .map(g => {
                const colors = get(COLOR_GROUPS_RAW, g.color, COLOR_GROUPS_RAW.default);
                const colorClasses = Object.values(colors).join(' ');

                return (
                  <div key={g.id}>
                    <div
                      className={`${colorClasses} flex items-center space-x-2 border-t border-b py-2 px-4`}
                    >
                      <GroupCheckbox
                        group={g}
                        selectedItemIds={selectedItemIds}
                        onToggle={() => toggleGroup(g)}
                      />
                      <span className="font-hmedium">{g.label}</span>
                    </div>
                    <div className="flex flex-col pl-4">
                      <div className="flex flex-col divide-y">
                        {g.children
                          .sort((a, b) => a.position - b.position)
                          .map(child => (
                            <Item
                              key={child.id}
                              item={child}
                              checked={selectedItemIds.includes(child.id)}
                              onClick={() => toggleItem(child.id)}
                            />
                          ))}
                      </div>
                    </div>
                  </div>
                );
              })}
        </div>
        <div className="flex-shrink-0 px-4 py-1 flex justify-between border-t items-center">
          <div className="flex-shrink">
            {['INTERNAL', 'PUBLIC'].includes(currentTaskItemFilter) && (
              <div className="text-sm my-4 text-slate-400 leading-[18px] flex-shrink flex truncate flex-col">
                <span className="truncate">
                  Affichage des démarches de la rubrique{' '}
                  {currentTaskItemFilter === 'INTERNAL'
                    ? "'Fiche de suivi'."
                    : "'Gestion de documents'."}
                </span>
                <button
                  className="text-blue-600 font-hmedium hover:underline"
                  type="button"
                  onClick={() => setCurrentTaskItemFilter('')}
                >
                  Afficher toutes les démarches
                </button>
              </div>
            )}
          </div>
          <div className="flex-shrink-0">
            <Button
              label={
                selectedItemIds.length <= 1
                  ? 'Ajouter'
                  : `Ajouter les ${selectedItemIds.length} éléments`
              }
              disabled={selectedItemIds.length <= 0}
              loading={isSubmitting}
              onClick={createTaskItems}
              color="blue"
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}